import { useFormContext } from 'react-hook-form';
import {
  CapitalContributionRule,
  CommitmentType,
  Vehicle,
} from 'further-types/lp/vehicle';
import { AddCommitmentsToEqualisationFormValues } from '../Editor/AddCommitmentsToEqualisation/FormWrapper';
import {
  calculateCPCapitalFromPercentage,
  getCommitmentsWithProRataCapitalContribution,
  calculateCapitalAndLoanForFixedCapitalContribution,
} from '../Editor/AddCommitmentsToEqualisation/utils';
import { sumBy } from 'lodash';

const useAddCommitmentsToEqualisations = (
  vehicle?: Vehicle,
  isEditingApprovedClose?: boolean,
) => {
  const { control, watch, setValue, getValues } =
    useFormContext<AddCommitmentsToEqualisationFormValues>();

  const formCommitments = watch('commitments');
  const baseCurrency = vehicle?.baseCurrency;

  const selectedCount =
    formCommitments?.filter((c) => c.isSelected).length ?? 0;
  const totalCount = formCommitments?.length ?? 0;
  const previouslyClosedCommitments = isEditingApprovedClose
    ? []
    : formCommitments?.filter((c) => c.equalisationDate);
  const pendingCloseCommitments = isEditingApprovedClose
    ? formCommitments
    : formCommitments?.filter((c) => !c.equalisationDate);
  const isAllSelected = totalCount > 0 && selectedCount === totalCount;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

  const isCapitalAndLoan =
    vehicle?.commitmentType === CommitmentType.CapitalAndLoan;
  const isFixedCapitalContribution =
    vehicle?.capitalContributionRule ===
    CapitalContributionRule.FixedCommitmentPercentage;
  const capitalContributionPercentage = vehicle?.capitalContributionPercentage;
  const isManualCapitalContribution =
    vehicle?.capitalContributionRule === CapitalContributionRule.Manual;
  const isProRataCapitalContribution =
    vehicle?.capitalContributionRule ===
    CapitalContributionRule.ProRataOfFixedPartnershipCapital;

  const handleSelectAll = (checked: boolean) => {
    if (formCommitments) {
      let carryPartnerIndex: number | undefined;
      formCommitments.forEach((commitment, index) => {
        if (!commitment.isCarryPartnerCommitment) {
          if (!isEditingApprovedClose && commitment.equalisationDate) {
            return;
          }
          setValue(`commitments.${index}.isSelected`, checked, {
            shouldValidate: true,
          });
        } else {
          carryPartnerIndex = index;
        }
      });

      if (isProRataCapitalContribution && carryPartnerIndex !== undefined) {
        calculateProRataCapitalContribution({
          index: carryPartnerIndex,
          isSelected: true,
        });
      } else {
        calculateCPCapitalContribution();
      }
    }
  };

  const calculateFixedCapitalContribution = (
    targetCommitment: number,
    index: number,
  ) => {
    const { capitalAmount, loanAmount } =
      calculateCapitalAndLoanForFixedCapitalContribution({
        targetCommitment,
        capitalContributionPercentage,
      });
    setValue(`commitments.${index}.capitalCommitmentLcy`, capitalAmount, {
      shouldValidate: true,
      shouldTouch: true,
    });
    setValue(`commitments.${index}.loanCommitmentLcy`, loanAmount, {
      shouldValidate: true,
      shouldTouch: true,
    });
    calculateCPCapitalContribution();
  };

  const calculateProRataCapitalContribution = ({
    totalCommitmentLcy,
    capitalCommitmentLcy,
    loanCommitmentLcy,
    index,
    resetToVehicleRules = false,
    isSelected,
  }: {
    totalCommitmentLcy?: number;
    capitalCommitmentLcy?: number;
    loanCommitmentLcy?: number;
    index: number;
    resetToVehicleRules?: boolean;
    isSelected?: boolean;
  }) => {
    const currentCommitments = getValues('commitments');
    const selectedCommitment = currentCommitments?.[index];
    if (!selectedCommitment) {
      return;
    }
    const selectedCommitmentIsCarryPartnerWithFixedCapitalContribution =
      selectedCommitment.isCarryPartnerCommitment &&
      !capitalContributionPercentage;

    const commitmentsWithNewValues = currentCommitments?.map((c) => {
      const cIsNotOverrideNorCarryPartner =
        !c.isCapitalContributionRuleOverride && !c.isCarryPartnerCommitment;

      if (c.id === selectedCommitment.id) {
        return {
          ...c,
          ...(totalCommitmentLcy !== undefined && { totalCommitmentLcy }),
          ...(capitalCommitmentLcy !== undefined && { capitalCommitmentLcy }),
          ...(loanCommitmentLcy !== undefined && { loanCommitmentLcy }),
          ...(isSelected !== undefined && { isSelected }),
          ...((resetToVehicleRules ||
            (isSelected && cIsNotOverrideNorCarryPartner)) && {
            isCapitalContributionRuleOverride: false,
            loanCommitmentLcy: c.isCarryPartnerCommitment
              ? c.loanCommitmentLcy
              : 0,
            capitalCommitmentLcy: 0,
          }),
        };
      }

      if (
        (isSelected || capitalCommitmentLcy) &&
        (selectedCommitment.isCapitalContributionRuleOverride ||
          selectedCommitmentIsCarryPartnerWithFixedCapitalContribution) &&
        cIsNotOverrideNorCarryPartner
      ) {
        return {
          ...c,
          loanCommitmentLcy: 0,
          capitalCommitmentLcy: 0,
        };
      }
      return c;
    });

    const commitmentsWithProRataCapitalContribution =
      getCommitmentsWithProRataCapitalContribution({
        commitments: commitmentsWithNewValues ?? [],
        vehicleFixedCapitalAmount: vehicle?.fixedCapitalAmount ?? 0,
        capitalContributionPercentage:
          vehicle?.carrySettings?.capitalContributionPercentage,
      });

    setValue('commitments', commitmentsWithProRataCapitalContribution, {
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const hasCarrySettingContributionPercentage =
    !!vehicle?.carrySettings?.capitalContributionPercentage;

  const isCPAndUsesCCPercentage = (
    isCarryPartnerCommitment: boolean | undefined,
  ) => {
    return isCarryPartnerCommitment && hasCarrySettingContributionPercentage;
  };

  const calculateCPCapitalContribution = () => {
    const currentCommitments = getValues('commitments');
    const carryPartnerIndex = currentCommitments?.findIndex(
      (c) => c.isCarryPartnerCommitment,
    );
    const carryPartner =
      carryPartnerIndex !== undefined && carryPartnerIndex !== -1
        ? currentCommitments[carryPartnerIndex]
        : undefined;

    if (
      !carryPartner ||
      !hasCarrySettingContributionPercentage ||
      carryPartner.isCapitalContributionRuleOverride
    ) {
      return;
    }

    const nonCarryPartnerSelectedCommitments = currentCommitments?.filter(
      (c) => !c.isCarryPartnerCommitment && c.isSelected,
    );

    const capitalContributionAmountLcy = calculateCPCapitalFromPercentage({
      capitalContributionPercentage:
        vehicle?.carrySettings?.capitalContributionPercentage ?? 0,
      nonCarryPartnerCapitalContributionTotalLcy:
        sumBy(nonCarryPartnerSelectedCommitments, 'capitalCommitmentLcy') ?? 0,
    });

    setValue(
      `commitments.${carryPartnerIndex}.capitalCommitmentLcy`,
      capitalContributionAmountLcy ?? 0,
      {
        shouldValidate: true,
        shouldTouch: true,
      },
    );
    setValue(
      `commitments.${carryPartnerIndex}.totalCommitmentLcy`,
      capitalContributionAmountLcy + (carryPartner.loanCommitmentLcy ?? 0),
      {
        shouldValidate: true,
        shouldTouch: true,
      },
    );
  };

  const handleOnChangeIsSelected = (index: number, isSelected: boolean) => {
    if (isProRataCapitalContribution) {
      calculateProRataCapitalContribution({
        index,
        isSelected,
      });
    } else {
      calculateCPCapitalContribution();
    }
  };

  const handleToggleIsSelected = (index: number) => {
    const currentValue = getValues(`commitments.${index}.isSelected`);
    const newValue = !currentValue;
    setValue(`commitments.${index}.isSelected`, newValue, {
      shouldValidate: true,
    });
    handleOnChangeIsSelected(index, newValue);
  };

  const capitalCommitmentIsEditable = (
    isCapitalContributionRuleOverride: boolean | undefined,
    isCarryPartnerCommitment: boolean | undefined,
  ) => {
    if (isCPAndUsesCCPercentage(isCarryPartnerCommitment)) {
      return !!isCapitalContributionRuleOverride;
    }
    if (isCarryPartnerCommitment) {
      return true;
    }
    return !!isCapitalContributionRuleOverride || isManualCapitalContribution;
  };

  const capitalCommitmentOnChange = (
    index: number,
    value: number,
    loanCommitmentLcy: number,
  ) => {
    if (isProRataCapitalContribution) {
      calculateProRataCapitalContribution({
        totalCommitmentLcy: value + (loanCommitmentLcy ?? 0),
        capitalCommitmentLcy: value,
        index,
      });
    } else {
      setValue(`commitments.${index}.capitalCommitmentLcy`, value, {
        shouldValidate: true,
        shouldTouch: true,
      });
      setValue(
        `commitments.${index}.totalCommitmentLcy`,
        value + (loanCommitmentLcy ?? 0),
        {
          shouldValidate: true,
          shouldTouch: true,
        },
      );
      if (hasCarrySettingContributionPercentage) {
        calculateCPCapitalContribution();
      }
    }
  };

  const loanCommitmentIsEditable = (
    isCapitalContributionRuleOverride: boolean | undefined,
    isCarryPartnerCommitment: boolean | undefined,
  ) => {
    return (
      !!isCapitalContributionRuleOverride ||
      isManualCapitalContribution ||
      !!isCarryPartnerCommitment
    );
  };

  const loanCommitmentOnChange = (
    index: number,
    value: number,
    capitalCommitmentLcy: number,
  ) => {
    if (isProRataCapitalContribution) {
      calculateProRataCapitalContribution({
        totalCommitmentLcy: value + (capitalCommitmentLcy ?? 0),
        loanCommitmentLcy: value,
        index,
      });
    } else {
      setValue(`commitments.${index}.loanCommitmentLcy`, value, {
        shouldValidate: true,
        shouldTouch: true,
      });
      setValue(
        `commitments.${index}.totalCommitmentLcy`,
        value + (capitalCommitmentLcy ?? 0),
        {
          shouldValidate: true,
          shouldTouch: true,
        },
      );
    }
  };

  const totalCommitmentIsEditable = (
    isCapitalContributionRuleOverride: boolean | undefined,
    isCarryPartnerCommitment: boolean | undefined,
  ) => {
    return (
      !isManualCapitalContribution &&
      !isCapitalContributionRuleOverride &&
      !isCarryPartnerCommitment
    );
  };

  const totalCommitmentOnChange = (index: number, value: number) => {
    if (isProRataCapitalContribution) {
      calculateProRataCapitalContribution({ totalCommitmentLcy: value, index });
    } else {
      setValue(`commitments.${index}.totalCommitmentLcy`, value, {
        shouldValidate: true,
        shouldTouch: true,
      });
      if (isFixedCapitalContribution && capitalContributionPercentage) {
        calculateFixedCapitalContribution(value, index);
      }
    }
  };

  const capitalContributionRuleOverrideOnChange = (
    index: number,
    value: boolean,
    totalCommitmentLcy: number,
    isCarryPartnerCommitment: boolean | undefined,
  ) => {
    if (!value) {
      if (isFixedCapitalContribution && capitalContributionPercentage) {
        calculateFixedCapitalContribution(totalCommitmentLcy, index);
      } else if (isProRataCapitalContribution) {
        calculateProRataCapitalContribution({
          totalCommitmentLcy,
          index,
          resetToVehicleRules: true,
        });
      } else if (isCPAndUsesCCPercentage(isCarryPartnerCommitment)) {
        calculateCPCapitalContribution();
      }
    }
  };

  const showOverrideColumn =
    !isManualCapitalContribution || hasCarrySettingContributionPercentage;

  const showOverrideCheckbox = (
    isCarryPartnerCommitment: boolean | undefined,
  ) => {
    return (
      isCPAndUsesCCPercentage(isCarryPartnerCommitment) ||
      (!isCarryPartnerCommitment && !isManualCapitalContribution)
    );
  };

  return {
    control,
    formCommitments,
    previouslyClosedCommitments,
    pendingCloseCommitments,
    baseCurrency,
    handleSelectAll,
    handleOnChangeIsSelected,
    handleToggleIsSelected,
    isAllSelected,
    isIndeterminate,
    isCapitalAndLoan,
    capitalCommitmentIsEditable,
    capitalCommitmentOnChange,
    loanCommitmentIsEditable,
    loanCommitmentOnChange,
    totalCommitmentIsEditable,
    totalCommitmentOnChange,
    capitalContributionRuleOverrideOnChange,
    showOverrideColumn,
    showOverrideCheckbox,
  };
};

export default useAddCommitmentsToEqualisations;
